/* Every word on the SteffiCare launch page, in one place, in both languages.
   The two bundles are structurally identical: the sections index into them by
   position (chapters, cards, steps), so a key that exists in `de` must exist
   in `en` with the same shape. */

import { usePublicLang } from "../../config/i18n/public";

const de = {
  HERO: {
    eyebrow: "Kein Gerät. Eine Begleitung.",
    title: ["Steffi.", "Der digitale Begleiter", "für Senioren."],
    emailPlaceholder: "Deine E-Mail-Adresse",
    emailLabel: "Verpasse nichts",
    emailSubmit: "Zur Warteliste",
    emailInvalid: "Diese E-Mail-Adresse sieht unvollständig aus. Bitte prüfe sie noch einmal.",
    body:
      "Steffi begleitet ältere Menschen durch Gespräche, kognitive Übungen, " +
      "Bewegung, Erinnerungen und alltägliche Unterstützung. Einfach per Sprache.",
    primary: "Zur Warteliste",
    secondary: "Demo ansehen",
    scroll: "Scrollen",
    alt: "SteffiCare Bot mit freundlichem Gesicht auf dem Display, Frontansicht",
  },

  VOICE: {
    eyebrow: "Natürlich sprechen",
    title: ["Einfach Steffi rufen.", "Mehr braucht es nicht."],
    subtitle: "Keine Befehle. Keine komplizierten Menüs. Einfach sprechen.",
    alt: "Steffi von vorn, Nahaufnahme des runden Displays",
    cards: [
      {
        icon: "ease",
        title: "Ohne Vorkenntnisse",
        body: "Ideal für Menschen, die wenig Erfahrung mit Technik haben.",
        detail:
          "Kein Konto, kein Menü, keine Einstellungen. Steffi steht bereit und " +
          "wartet darauf, angesprochen zu werden, so wie ein Mensch im Raum.",
      },
      {
        icon: "memory",
        title: "Steffi erinnert sich",
        body: "An Interessen, Gewohnheiten und frühere Gespräche.",
        detail:
          "Wer gern Skat spielt, wird nach dem Skatabend gefragt. Wer jeden " +
          "Sonntag telefoniert, wird sonntags daran erinnert.",
      },
      {
        icon: "ask",
        title: "Steffi fragt nach",
        body: "Sie führt Gespräche weiter und macht Unterhaltungen persönlicher.",
        detail:
          "Statt auf den nächsten Befehl zu warten, hakt Steffi nach, greift " +
          "Gesagtes wieder auf und hält das Gespräch am Laufen.",
      },
    ],
  },

  /* The things people actually say to her. Each one rises to the centre of the
     screen, holds, and drifts away as the next arrives. */
  ASKS: [
    {
      icon: "call",
      wake: "Steffi,",
      query: "ruf meine Tochter an.",
      body: "Videotelefonie mit einem Satz. Ohne Nummer, ohne Menü.",
    },
    {
      icon: "music",
      wake: "Steffi,",
      query: "spiel Musik von früher.",
      body: "Lieblingslieder und Radiosender, laut genug für den ganzen Raum.",
    },
    {
      icon: "weather",
      wake: "Steffi,",
      query: "wie wird das Wetter heute?",
      body: "Wetter, Uhrzeit und Datum, jederzeit auf Zuruf.",
    },
    {
      icon: "news",
      wake: "Steffi,",
      query: "was gibt es Neues?",
      body: "Nachrichten und Tagesschau, vorgelesen statt gelesen.",
    },
    {
      icon: "photo",
      wake: "Steffi,",
      query: "zeig mir Fotos von den Enkeln.",
      body: "Neue Bilder aus der Familie erscheinen direkt auf dem Display.",
    },
    {
      icon: "recipe",
      wake: "Steffi,",
      query: "wie geht Kartoffelsuppe?",
      body: "Rezepte Schritt für Schritt, im Tempo der Küche.",
    },
    {
      icon: "play",
      wake: "Steffi,",
      query: "lass uns etwas spielen.",
      body: "Quiz, Wortspiele und Gedächtnisübungen, komplett per Sprache.",
    },
  ],

  CALL: {
    title: ["Immer nur einen", "Anruf entfernt."],
    subtitle: "Videotelefonie, die sich nicht nach Technik anfühlt.",
    callerLine: "Enkelin",
    fromApp: {
      title: "Ein Tipp genügt.",
      body: "Video, Anruf oder Durchsage, direkt aus der App der Angehörigen.",
    },
    states: {
      ringing: "Emma ruft an",
      connected: "Verbunden",
    },
    specs: [
      { icon: "display", mark: "5 Zoll", title: "5 Zoll Display", body: "Ein runder HD Bildschirm, groß genug für ein ganzes Gesicht." },
      { icon: "camera", mark: "12 MP", title: "12 MP Kamera", body: "Alle am Tisch sind im Bild. Ohne Zurechtrücken." },
      { icon: "press", mark: "Einmal", title: "Einmal drücken", body: "Zum Abheben einfach auf Steffi drücken. Das ist alles." },
    ],
    hardware: {
      eyebrow: "Spracherkennung",
      title: ["Natürlich sprechen.", "Von überall im Raum."],
      features: [
        {
          icon: "languages",
          lead: "Steffi unterstützt",
          highlight: "über 10 Sprachen",
          mark: "10",
        },
        {
          icon: "distance",
          lead: "Versteht Stimmen aus",
          highlight: "bis zu fünf Metern Entfernung",
          mark: "fünf",
        },
      ],
    },
    alts: {
      call: "Steffi nimmt einen Videoanruf entgegen",
      app: "Profilansicht der SteffiCare App mit den Schaltflächen Video, Anruf und Durchsage",
      scene: "Ältere Frau im Videoanruf mit ihrer Tochter, Steffi steht vor ihr auf dem Esstisch",
      side: "SteffiCare Bot in der Dreiviertelansicht mit lächelndem Gesicht auf dem Display, die Mikrofone hören den ganzen Raum",
    },
  },

  ACTIVATION: {
    eyebrow: "Aktivierung",
    title: ["Jeder Tag", "ein bisschen aktiver."],
    subtitle: "Spiele interaktive Übungen mit Steffi.",
    cards: [
      {
        title: "Kognitive Übungen",
        body: "Steffi trainiert Gedächtnis, Sprache und Aufmerksamkeit.",
        icon: "cognitive",
        image: "/steffi/activation-cognitive.webp",
        imagePosition: "72% center",
        imageAlt: "Ältere Frau löst am Esstisch ein Worträtsel auf Steffis Display",
      },
      {
        title: "Körperliche Übungen",
        body: "Steffi motiviert täglich zu mehr Bewegung.",
        icon: "physical",
        image: "/steffi/activation-physical.webp",
        imagePosition: "60% 30%",
        imageAlt: "Ältere Frau macht im Wohnzimmer eine Dehnübung, Steffi zeigt die Übung",
      },
    ],
    /* the floating chips around the device, in the order of ORBIT in
       ActivationSection — the icons and angles live there */
    suggestions: [
      "Gedächtnis",
      "Bewegung",
      "Musik",
      "Gespräch",
      "Wohlbefinden",
      "Nachrichten",
      "Wetter",
    ],
    game: {
      eyebrow: "Übungen aus der Ferne planen",
      title: "Aktivierung, die sich dem Alltag anpasst.",
      body:
        "Angehörige wählen in der Family App mentale, kognitive und körperliche " +
        "Übungen aus und bestimmen, wie oft Steffi sie anbieten soll. Gleichzeitig " +
        "kann die Person jede Übung jederzeit selbst starten, ganz ohne Hilfe.",
      points: ["Remote eingerichtet", "Häufigkeit frei wählbar", "Jederzeit selbst startbar"],
    },
    alts: {
      device: "Steffi zeigt wechselnde Spiele und Informationen auf ihrem Display",
      pick: "Auswahl mentaler, kognitiver und körperlicher Aktivitäten in der SteffiCare App",
      confirm: "Bestätigung einer regelmäßig geplanten Kreuzworträtselübung",
    },
  },

  APP: {
    eyebrow: "Die Begleit App",
    title: ["Für Angehörige", "entwickelt."],
    intro:
      "Über die Family App bleibt ihr jederzeit verbunden: Nehmt direkt Kontakt auf, " +
      "plant Tagesroutinen, erstellt Erinnerungen und Kalendereinträge und sorgt so " +
      "für mehr Sicherheit, Struktur und Nähe im Alltag.",
    trioImage: "/steffi/family-app-phones-v2.webp",
    chapters: [
      {
        shot: "feed",
        eyebrow: "Familien Feed",
        title: "Familienmomente teilen.",
        body: "Fotos. Videos. Nachrichten. Sprachnachrichten. Steffi zeigt alles direkt an.",
        points: ["Fotos & Videos", "Sprachnachrichten", "Ohne App Kenntnisse abrufbar"],
      },
      {
        shot: "calendar",
        eyebrow: "Kalender",
        title: "Gemeinsam organisieren.",
        body: "Steffi erinnert zuverlässig und sagt Bescheid, bevor es losgeht.",
        points: ["Arzttermine", "Geburtstage", "Besuche", "Routinen"],
      },
      {
        shot: "reminders",
        eyebrow: "Erinnerungen",
        title: "Erinnerungen, die wirklich ankommen.",
        body:
          "In der App angelegt, von Steffi ausgesprochen. Laut, freundlich und " +
          "so oft, wie es nötig ist.",
        points: ["Medikamente", "Trinken", "Aufstehen & Bewegung"],
      },
      {
        shot: "routines",
        eyebrow: "Routinen",
        title: "Der Tag bekommt einen Rhythmus.",
        body:
          "Angehörige wählen aus, was Steffi regelmäßig anbieten soll, von " +
          "Unterhaltung bis zum Gehirnjogging.",
        points: ["Unterhaltung", "Gesundheit", "Gehirnjogging"],
      },
    ],
    /* the bubbles that travel from the phone to Steffi, by chapter */
    handoffs: {
      feed: "Erik Nachricht",
      routines: "Biete Übung an",
    },
    alts: {
      trio: "Drei Ansichten der SteffiCare Family App: Durchsage, Familien Feed und Routinen",
      phone: (title) => `${title} in der SteffiCare App`,
    },
  },

  /* The reminders that travel from the app to Steffi, in order. `screen` is the
     round display she switches to the moment the reminder lands. */
  REMINDERS: [
    { key: "medication", icon: "pills", label: "16:00 · Tabletten", screen: "medication" },
    { key: "oven", icon: "bell", label: "17:00 · Ofen ausmachen", screen: "oven" },
    { key: "timer", icon: "timer", label: "Timer · 2 Minuten", screen: "timer" },
    { key: "drink", icon: "drink", label: "Zeit zu trinken", screen: "drink" },
  ],

  /* Wohlbefinden & Sicherheit — three chapters, one message and one dominant
     visual each. Peace of mind and early awareness, never diagnosis. */
  WELLBEING: {
    eyebrow: "Wohlbefinden & Sicherheit",
    missingShot: "Screenshot fehlt",

    help: {
      title: ["Unterstützung,", "wenn sie gebraucht wird."],
      subtitle: "Wenn Hilfe benötigt wird, genügt ein einfacher Sprachbefehl.",
      steps: [
        { label: "„Hilfe Steffi“", tone: "trigger" },
        { label: "Steffi bestätigt den Befehl" },
        { label: "Anruf Kontakt 1" },
        { label: "Keine Antwort", tone: "muted" },
        { label: "Anruf Kontakt 2" },
        { label: "Verbunden", tone: "done" },
      ],
      note:
        "Bei „Hilfe Steffi“ ruft Steffi automatisch hinterlegte Notfallkontakte " +
        "nacheinander an, bis jemand erreichbar ist.",
      alts: {
        scene: "Ältere Frau sitzt nach einem Sturz auf dem Wohnzimmerteppich, Steffi auf dem Couchtisch setzt den Notruf ab",
        shot: "Notruf Ansicht der SteffiCare Family App",
      },
    },

    activity: {
      title: ["Aktivität", "auf einen Blick."],
      subtitle:
        "Behalten Sie im Blick, wie regelmäßig Ihre Angehörigen mit Steffi interagieren.",
      blocks: [
        {
          icon: "talk",
          title: "Gespräche",
          body: "Zeigt, wie regelmäßig Gespräche mit Steffi stattfinden.",
        },
        {
          icon: "brain",
          title: "Kognitive Übungen",
          body: "Verfolgen Sie die Teilnahme an Gedächtnis und Sprachübungen.",
        },
        {
          icon: "move",
          title: "Körperliche Aktivität",
          body: "Erkennen Sie langfristige Veränderungen der täglichen Aktivität.",
        },
      ],
      alt: "Aktivitätsdashboard der SteffiCare Family App",
    },

    speech: {
      title: ["Veränderungen", "frühzeitig erkennen."],
      subtitle:
        "Steffi analysiert langfristige Veränderungen der Sprachinteraktion und " +
        "macht Entwicklungen über digitale Biomarker sichtbar.",
      cards: [
        {
          icon: "mic",
          title: "Sprachanalyse",
          body: "Analyse langfristiger Veränderungen der Sprachinteraktion.",
        },
        {
          icon: "brain",
          title: "Digitale Biomarker",
          // the Stimm-Analyse screen beside this card reads "5 KATEGORIEN · 20 BIOMARKER"
          body: "Auswertung von 20 sprachbasierten Biomarkern in fünf Kategorien.",
          mark: ["20", "fünf"],
        },
        {
          icon: "trend",
          title: "Langfristige Trends",
          body: "Entwicklungen werden über die Zeit sichtbar.",
        },
        {
          icon: "family",
          title: "Einblicke für Angehörige",
          body: "Unterstützt Angehörige dabei, Veränderungen frühzeitig wahrzunehmen.",
        },
      ],
      disclaimer:
        "Die bereitgestellten Informationen dienen ausschließlich der Unterstützung " +
        "und ersetzen keine medizinische Diagnose.",
      alt: "Biomarkeranalyse der SteffiCare Family App",
    },
  },

  LIVING: {
    lead: "Kein Bildschirm, den man bedienen muss.",
    trail: "Ein Gegenüber, mit dem man spricht.",
    alt: "Steffi mit freundlichem Gesicht auf dem Esstisch im Morgenlicht",
  },

  BLOG: {
    eyebrow: "Aus dem Blog",
    title: "Gedanken, die weiterhelfen.",
    all: "Alle Beiträge",
  },

  NAV: {
    blog: "Blog",
    about: "Über uns",
    login: "Anmelden",
    contact: "Kontakt",
    careHomes: "Für Pflegeheime",
    facilities: "Für Einrichtungen",
    families: "Für Angehörige",
    cta: "Reservieren",
    language: "Sprache",
    menu: "Menü",
  },

  WAITLIST: {
    eyebrow: "Warteliste",
    title: ["Steffi kommt bald", "zu euch nach Hause."],
    body:
      "Wir geben dir Bescheid, sobald es losgeht. Bis dahin halten wir dich über " +
      "SteffiCare auf dem Laufenden – mit neuen Perspektiven, Impulsen und Wissenswertem " +
      "rund um Healthy Ageing und die Zukunft des Älterwerdens.",
    placeholder: "deine@email.de",
    consentLead: "Ich möchte über den Start informiert werden und habe die",
    consentLink: "Datenschutzhinweise",
    consentTrail: "gelesen.",
    consentRequired: "Bitte bestätige noch kurz dein Einverständnis – sonst dürfen wir dich nicht informieren.",
    emailRequired: "Bitte gib eine vollständige E-Mail-Adresse ein.",
    submit: "Eintragen",
    sending: "Wird gesendet …",
    done: "Danke. Wir melden uns, sobald Steffi verfügbar ist.",
    error: "Das hat leider nicht geklappt.",
    close: "Schließen",
  },

  FINALE: {
    title: ["Mehr Nähe.", "Mehr Selbstständigkeit.", "Mehr Lebensqualität."],
    body:
      "Steffi verbindet Familien, aktiviert ältere Menschen und macht moderne " +
      "Technologie endlich einfach.",
    primary: "Zur Warteliste",
    secondary: "Demo vereinbaren",
    card: {
      eyebrow: "Warteliste",
      title: "Steffi zieht bald bei euch ein.",
      body: "Trag dich ein und erfahre als Erstes, wann es losgeht.",
    },
    alt: "Steffi schaut den Besucher an",
  },

  FOOTER: {
    tagline: "Steffi ist kein Gerät. Steffi ist eine Begleitung.",
    social_label: "Folgen Sie uns",
    social: [
      { label: "Instagram", href: "https://www.instagram.com/stefficare/", icon: "instagram" },
      { label: "Facebook", href: "https://www.facebook.com/stefficare", icon: "facebook" },
      { label: "LinkedIn", href: "https://www.linkedin.com/company/stefficare/", icon: "linkedin" },
      { label: "YouTube", href: "https://www.youtube.com/@stefficare_official", icon: "youtube" },
    ],
    legal: [
      { label: "Impressum", to: "/impressum" },
      { label: "Datenschutz", to: "/datenschutzhinweis" },
      { label: "Nutzungsbedingungen", to: "/nutzungsbedingungen" },
    ],
  },

  documentTitle: "Steffi. Der digitale Begleiter für Senioren | SteffiCare",
};

const en = {
  HERO: {
    eyebrow: "Not a device. A companion.",
    title: ["Steffi.", "The digital companion", "for older adults."],
    emailPlaceholder: "Your email address",
    emailLabel: "Don't miss a thing",
    emailSubmit: "Join the waitlist",
    emailInvalid: "This email address looks incomplete. Please check it again.",
    body:
      "Steffi supports older adults through conversation, cognitive exercises, " +
      "movement, reminders and everyday help. Simply by speaking.",
    primary: "Join the waitlist",
    secondary: "Watch the demo",
    scroll: "Scroll",
    alt: "SteffiCare bot with a friendly face on its display, front view",
  },

  VOICE: {
    eyebrow: "Speak naturally",
    title: ["Just call for Steffi.", "That is all it takes."],
    subtitle: "No commands. No complicated menus. Just speak.",
    alt: "Steffi from the front, close up of the round display",
    cards: [
      {
        icon: "ease",
        title: "No experience needed",
        body: "Ideal for people with little experience of technology.",
        detail:
          "No account, no menu, no settings. Steffi is simply there, waiting to " +
          "be spoken to, like another person in the room.",
      },
      {
        icon: "memory",
        title: "Steffi remembers",
        body: "Interests, habits and earlier conversations.",
        detail:
          "Someone who loves their card evenings gets asked how the game went. " +
          "Someone who calls every Sunday is reminded on Sunday.",
      },
      {
        icon: "ask",
        title: "Steffi asks back",
        body: "She keeps conversations going and makes them more personal.",
        detail:
          "Instead of waiting for the next command, Steffi follows up, picks up " +
          "on what was said and keeps the conversation alive.",
      },
    ],
  },

  ASKS: [
    {
      icon: "call",
      wake: "Steffi,",
      query: "call my daughter.",
      body: "A video call in one sentence. No number, no menu.",
    },
    {
      icon: "music",
      wake: "Steffi,",
      query: "play music from back then.",
      body: "Favourite songs and radio stations, loud enough for the whole room.",
    },
    {
      icon: "weather",
      wake: "Steffi,",
      query: "what is the weather today?",
      body: "Weather, time and date, whenever you ask.",
    },
    {
      icon: "news",
      wake: "Steffi,",
      query: "what is in the news?",
      body: "The day's headlines, read aloud instead of read.",
    },
    {
      icon: "photo",
      wake: "Steffi,",
      query: "show me photos of the grandchildren.",
      body: "New family pictures appear straight on the display.",
    },
    {
      icon: "recipe",
      wake: "Steffi,",
      query: "how do I make potato soup?",
      body: "Recipes step by step, at the pace of the kitchen.",
    },
    {
      icon: "play",
      wake: "Steffi,",
      query: "let us play something.",
      body: "Quizzes, word games and memory exercises, entirely by voice.",
    },
  ],

  CALL: {
    title: ["Always just one", "call away."],
    subtitle: "Video calling that does not feel like technology.",
    callerLine: "Granddaughter",
    fromApp: {
      title: "One tap is enough.",
      body: "Video, call or announcement, straight from the family's app.",
    },
    states: {
      ringing: "Emma is calling",
      connected: "Connected",
    },
    specs: [
      { icon: "display", mark: "5 inch", title: "5 inch display", body: "A round HD screen, big enough for a whole face." },
      { icon: "camera", mark: "12 MP", title: "12 MP camera", body: "Everyone at the table is in frame. No shuffling around." },
      { icon: "press", mark: "One", title: "One press", body: "To answer, just press Steffi. That is all." },
    ],
    hardware: {
      eyebrow: "Speech recognition",
      title: ["Speak naturally.", "From anywhere in the room."],
      features: [
        {
          icon: "languages",
          lead: "Steffi supports",
          highlight: "over 10 languages",
          mark: "10",
        },
        {
          icon: "distance",
          lead: "Understands voices from",
          highlight: "up to five metres away",
          mark: "five",
        },
      ],
    },
    alts: {
      call: "Steffi taking a video call",
      app: "Profile view of the SteffiCare app with the Video, Call and Announcement buttons",
      scene: "Older woman on a video call with her daughter, Steffi on the dining table in front of her",
      side: "SteffiCare bot in three quarter view with a smiling face on its display, the microphones hear the whole room",
    },
  },

  ACTIVATION: {
    eyebrow: "Activation",
    title: ["Every day", "a little more active."],
    subtitle: "Play interactive exercises with Steffi.",
    cards: [
      {
        title: "Cognitive exercises",
        body: "Steffi trains memory, language and attention.",
        icon: "cognitive",
        image: "/steffi/activation-cognitive.webp",
        imagePosition: "72% center",
        imageAlt: "Older woman solving a word puzzle on Steffi's display at the dining table",
      },
      {
        title: "Physical exercises",
        body: "Steffi encourages a little more movement every day.",
        icon: "physical",
        image: "/steffi/activation-physical.webp",
        imagePosition: "60% 30%",
        imageAlt: "Older woman doing a stretch in the living room, Steffi showing the exercise",
      },
    ],
    suggestions: [
      "Memory",
      "Movement",
      "Music",
      "Conversation",
      "Wellbeing",
      "News",
      "Weather",
    ],
    game: {
      eyebrow: "Plan exercises from anywhere",
      title: "Activation that fits into everyday life.",
      body:
        "In the Family App, relatives choose mental, cognitive and physical " +
        "exercises and decide how often Steffi should offer them. At the same " +
        "time the person can start any exercise themselves, at any moment, " +
        "entirely without help.",
      points: ["Set up remotely", "Frequency freely chosen", "Can be started alone anytime"],
    },
    alts: {
      device: "Steffi showing changing games and information on her display",
      pick: "Choosing mental, cognitive and physical activities in the SteffiCare app",
      confirm: "Confirming a regularly scheduled crossword exercise",
    },
  },

  APP: {
    eyebrow: "The companion app",
    title: ["Built for", "the family."],
    intro:
      "The Family App keeps you connected at all times: get in touch directly, " +
      "plan daily routines, create reminders and calendar entries, for more " +
      "safety, structure and closeness in everyday life.",
    trioImage: "/steffi/family-app-phones-en.webp",
    chapters: [
      {
        shot: "feed",
        eyebrow: "Family feed",
        title: "Sharing family moments.",
        body: "Photos. Videos. Messages. Voice messages. Steffi shows it all straight away.",
        points: ["Photos & videos", "Voice messages", "No app skills needed"],
      },
      {
        shot: "calendar",
        eyebrow: "Calendar",
        title: "Organising together.",
        body: "Steffi reminds reliably and says so before it starts.",
        points: ["Doctor's appointments", "Birthdays", "Visits", "Routines"],
      },
      {
        shot: "reminders",
        eyebrow: "Reminders",
        title: "Reminders that actually land.",
        body:
          "Created in the app, spoken by Steffi. Loud, friendly and as often " +
          "as it takes.",
        points: ["Medication", "Drinking", "Getting up & moving"],
      },
      {
        shot: "routines",
        eyebrow: "Routines",
        title: "The day finds a rhythm.",
        body:
          "Relatives choose what Steffi should offer regularly, from entertainment " +
          "to brain training.",
        points: ["Entertainment", "Health", "Brain training"],
      },
    ],
    handoffs: {
      feed: "Message from Erik",
      routines: "Offering an exercise",
    },
    alts: {
      trio: "Three views of the SteffiCare Family App: announcement, family feed and routines",
      phone: (title) => `${title} in the SteffiCare app`,
    },
  },

  REMINDERS: [
    { key: "medication", icon: "pills", label: "4:00 PM · Pills", screen: "medication" },
    { key: "oven", icon: "bell", label: "5:00 PM · Turn off the oven", screen: "oven" },
    { key: "timer", icon: "timer", label: "Timer · 2 minutes", screen: "timer" },
    { key: "drink", icon: "drink", label: "Time for a drink", screen: "drink" },
  ],

  WELLBEING: {
    eyebrow: "Wellbeing & safety",
    missingShot: "Screenshot missing",

    help: {
      title: ["Support,", "the moment it is needed."],
      subtitle: "When help is needed, a simple voice command is enough.",
      steps: [
        { label: "“Help Steffi”", tone: "trigger" },
        { label: "Steffi confirms the command" },
        { label: "Calling contact 1" },
        { label: "No answer", tone: "muted" },
        { label: "Calling contact 2" },
        { label: "Connected", tone: "done" },
      ],
      note:
        "On “Help Steffi”, Steffi automatically calls the stored emergency " +
        "contacts one after another until someone answers.",
      alts: {
        scene: "Older woman sitting on the living room rug after a fall, Steffi on the coffee table placing the emergency call",
        shot: "Emergency call view of the SteffiCare Family App",
      },
    },

    activity: {
      title: ["Activity", "at a glance."],
      subtitle:
        "Keep an eye on how regularly your relative interacts with Steffi.",
      blocks: [
        {
          icon: "talk",
          title: "Conversations",
          body: "Shows how regularly conversations with Steffi take place.",
        },
        {
          icon: "brain",
          title: "Cognitive exercises",
          body: "Follow participation in memory and language exercises.",
        },
        {
          icon: "move",
          title: "Physical activity",
          body: "Spot long term changes in daily activity.",
        },
      ],
      alt: "Activity dashboard of the SteffiCare Family App",
    },

    speech: {
      title: ["Noticing changes", "early."],
      subtitle:
        "Steffi analyses long term changes in speech interaction and makes " +
        "developments visible through digital biomarkers.",
      cards: [
        {
          icon: "mic",
          title: "Speech analysis",
          body: "Analysis of long term changes in speech interaction.",
        },
        {
          icon: "brain",
          title: "Digital biomarkers",
          // the voice-analysis screen beside this card reads "5 CATEGORIES · 20 BIOMARKERS"
          body: "Evaluation of 20 speech based biomarkers across five categories.",
          mark: ["20", "five"],
        },
        {
          icon: "trend",
          title: "Long term trends",
          body: "Developments become visible over time.",
        },
        {
          icon: "family",
          title: "Insights for relatives",
          body: "Helps relatives notice changes early.",
        },
      ],
      disclaimer:
        "The information provided is intended purely as support and does not " +
        "replace a medical diagnosis.",
      alt: "Biomarker analysis of the SteffiCare Family App",
    },
  },

  LIVING: {
    lead: "No screen to operate.",
    trail: "Someone to talk to.",
    alt: "Steffi with a friendly face on the dining table in morning light",
  },

  BLOG: {
    eyebrow: "From the blog",
    title: "Thoughts that help.",
    all: "All posts",
  },

  NAV: {
    blog: "Blog",
    about: "About us",
    menu: "Menu",
    login: "Sign in",
    contact: "Contact",
    careHomes: "For care homes",
    facilities: "For facilities",
    families: "For families",
    cta: "Reserve",
    language: "Language",
  },

  WAITLIST: {
    eyebrow: "Waitlist",
    title: ["Steffi is moving in", "with you soon."],
    body:
      "We will let you know as soon as we launch. Until then, we will keep you up to date " +
      "with SteffiCare – sharing new perspectives, ideas and useful information about " +
      "healthy ageing and the future of growing older.",
    placeholder: "your@email.com",
    consentLead: "I would like to hear about the launch and have read the",
    consentLink: "privacy notice",
    consentTrail: ".",
    consentRequired: "Please confirm your consent first – otherwise we are not allowed to contact you.",
    emailRequired: "Please enter a complete email address.",
    submit: "Sign up",
    sending: "Sending …",
    done: "Thank you. We will be in touch as soon as Steffi is available.",
    error: "That did not work, unfortunately.",
    close: "Close",
  },

  FINALE: {
    title: ["More closeness.", "More independence.", "More quality of life."],
    body:
      "Steffi connects families, keeps older people active and finally makes " +
      "modern technology simple.",
    primary: "Join the waitlist",
    secondary: "Book a demo",
    card: {
      eyebrow: "Waitlist",
      title: "Steffi is moving in soon.",
      body: "Sign up and be the first to know when we launch.",
    },
    alt: "Steffi looking at the visitor",
  },

  FOOTER: {
    tagline: "Steffi is not a device. Steffi is a companion.",
    social_label: "Follow us",
    social: [
      { label: "Instagram", href: "https://www.instagram.com/stefficare/", icon: "instagram" },
      { label: "Facebook", href: "https://www.facebook.com/stefficare", icon: "facebook" },
      { label: "LinkedIn", href: "https://www.linkedin.com/company/stefficare/", icon: "linkedin" },
      { label: "YouTube", href: "https://www.youtube.com/@stefficare_official", icon: "youtube" },
    ],
    legal: [
      { label: "Imprint", to: "/impressum" },
      { label: "Privacy", to: "/datenschutzhinweis" },
      { label: "Terms of use", to: "/nutzungsbedingungen" },
    ],
  },

  documentTitle: "Steffi. The digital companion for older adults | SteffiCare",
};

export const CONTENT = { de, en };

/* Every section reads its copy through this hook, so one switch in the nav
   re-renders the whole page in the other language. */
export function useSteffiCopy() {
  const { lang } = usePublicLang();
  return CONTENT[lang] || CONTENT.de;
}
